export interface IExpressionProperty {
  key: string;
  label: string;
  category:
    | 'visibility'
    | 'state'
    | 'appearance'
    | 'validation'
    | 'value'
    | 'custom';
  valueType: 'boolean' | 'string' | 'number' | 'any' | 'function';
  description: string;
}

export const STANDARD_EXPRESSION_PROPS: IExpressionProperty[] = [
  // Visibility
  {
    key: 'hide',
    label: 'Visibility (Hide)',
    category: 'visibility',
    valueType: 'boolean',
    description: 'Hide or show this field',
  },
  {
    key: 'props.hideField',
    label: 'Hide Field (CSS)',
    category: 'visibility',
    valueType: 'boolean',
    description: 'Hide field via CSS class (keeps in DOM)',
  },
  {
    key: 'props.hidden',
    label: 'Hidden',
    category: 'visibility',
    valueType: 'boolean',
    description: 'Mark field as hidden',
  },

  // State
  {
    key: 'props.disabled',
    label: 'Disabled',
    category: 'state',
    valueType: 'boolean',
    description: 'Disable this field',
  },
  {
    key: 'props.readonly',
    label: 'Read Only',
    category: 'state',
    valueType: 'boolean',
    description: 'Make this field read-only',
  },
  {
    key: 'props.required',
    label: 'Required',
    category: 'validation',
    valueType: 'boolean',
    description: 'Make this field required',
  },

  // Appearance
  {
    key: 'props.label',
    label: 'Label',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic field label',
  },
  {
    key: 'props.placeholder',
    label: 'Placeholder',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic placeholder text',
  },
  {
    key: 'props.description',
    label: 'Description',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic helper text',
  },
  {
    key: 'props.hint',
    label: 'Hint',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic hint text',
  },
  {
    key: 'props.tooltip',
    label: 'Tooltip',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic tooltip text',
  },
  {
    key: 'className',
    label: 'CSS Class',
    category: 'appearance',
    valueType: 'string',
    description: 'Dynamic CSS class',
  },
];

export const TYPE_EXPRESSION_PROPS: Record<string, IExpressionProperty[]> = {
  // ── Text Input ──
  input: [
    {
      key: 'props.type',
      label: 'Input Type',
      category: 'appearance',
      valueType: 'string',
      description: 'Input type (text, number, email, etc.)',
    },
    {
      key: 'props.minLength',
      label: 'Min Length',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic minimum length',
    },
    {
      key: 'props.maxLength',
      label: 'Max Length',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic maximum length',
    },
    {
      key: 'props.pattern',
      label: 'Pattern',
      category: 'validation',
      valueType: 'string',
      description: 'Dynamic regex pattern',
    },
    {
      key: 'props.min',
      label: 'Min Value',
      category: 'validation',
      valueType: 'number',
      description: 'Min value for number inputs',
    },
    {
      key: 'props.max',
      label: 'Max Value',
      category: 'validation',
      valueType: 'number',
      description: 'Max value for number inputs',
    },
  ],

  // ── Textarea ──
  textarea: [
    {
      key: 'props.rows',
      label: 'Rows',
      category: 'appearance',
      valueType: 'number',
      description: 'Number of visible rows',
    },
    {
      key: 'props.maxLength',
      label: 'Max Length',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic maximum length',
    },
    {
      key: 'props.minLength',
      label: 'Min Length',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic minimum length',
    },
  ],

  // ── Checkbox ──
  checkbox: [
    {
      key: 'props.indeterminate',
      label: 'Indeterminate',
      category: 'state',
      valueType: 'boolean',
      description: 'Set indeterminate state',
    },
  ],

  // ── Radio ──
  radio: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic radio options array',
    },
  ],

  // ── Multi Checkbox ──
  multicheckbox: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic checkbox options array',
    },
    {
      key: 'props.minSelection',
      label: 'Min Selection',
      category: 'validation',
      valueType: 'number',
      description: 'Minimum selections required',
    },
    {
      key: 'props.maxSelection',
      label: 'Max Selection',
      category: 'validation',
      valueType: 'number',
      description: 'Maximum selections allowed',
    },
  ],

  // ── Custom Dropdown ──
  customdropdown: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic dropdown options array',
    },
    {
      key: 'props.multiple',
      label: 'Multiple Select',
      category: 'state',
      valueType: 'boolean',
      description: 'Allow multiple selections',
    },
    {
      key: 'props.bindLabel',
      label: 'Bind Label',
      category: 'value',
      valueType: 'string',
      description: 'Property to use as label',
    },
    {
      key: 'props.bindValue',
      label: 'Bind Value',
      category: 'value',
      valueType: 'string',
      description: 'Property to use as value',
    },
    {
      key: 'props.searchable',
      label: 'Searchable',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable search in dropdown',
    },
  ],

  // ── Custom Dropdown Paginated ──
  customdropdownpaginated: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic dropdown options',
    },
    {
      key: 'props.multiple',
      label: 'Multiple Select',
      category: 'state',
      valueType: 'boolean',
      description: 'Allow multiple selections',
    },
    {
      key: 'props.bindLabel',
      label: 'Bind Label',
      category: 'value',
      valueType: 'string',
      description: 'Property to use as label',
    },
    {
      key: 'props.bindValue',
      label: 'Bind Value',
      category: 'value',
      valueType: 'string',
      description: 'Property to use as value',
    },
    {
      key: 'props.dataset',
      label: 'Dataset',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic dataset configuration',
    },
    {
      key: 'props.useBaseUrl',
      label: 'Use Base URL',
      category: 'value',
      valueType: 'boolean',
      description: 'Use base API URL',
    },
  ],

  // ── Cascading Dropdowns ──
  customcascadingdropdowns: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic cascading options',
    },
  ],

  // ── Select ──
  select: [
    {
      key: 'props.options',
      label: 'Options',
      category: 'value',
      valueType: 'any',
      description: 'Dynamic select options array',
    },
    {
      key: 'props.multiple',
      label: 'Multiple Select',
      category: 'state',
      valueType: 'boolean',
      description: 'Allow multiple selections',
    },
  ],

  // ── Date Picker ──
  customdatepicker: [
    {
      key: 'props.minDate',
      label: 'Min Date',
      category: 'validation',
      valueType: 'any',
      description: 'Dynamic minimum date',
    },
    {
      key: 'props.maxDate',
      label: 'Max Date',
      category: 'validation',
      valueType: 'any',
      description: 'Dynamic maximum date',
    },
    {
      key: 'props.dateFormat',
      label: 'Date Format',
      category: 'appearance',
      valueType: 'string',
      description: 'Date display format',
    },
    {
      key: 'props.delimeter',
      label: 'Delimiter',
      category: 'appearance',
      valueType: 'string',
      description: 'Date delimiter (/ or -)',
    },
  ],

  // ── DateTime Picker ──
  customdatetimepicker: [
    {
      key: 'props.minDate',
      label: 'Min Date',
      category: 'validation',
      valueType: 'any',
      description: 'Dynamic minimum date',
    },
    {
      key: 'props.maxDate',
      label: 'Max Date',
      category: 'validation',
      valueType: 'any',
      description: 'Dynamic maximum date',
    },
  ],

  // ── Time Picker ──
  customtimepicker: [
    {
      key: 'props.startHour',
      label: 'Start Hour',
      category: 'validation',
      valueType: 'number',
      description: 'Start hour for time range',
    },
    {
      key: 'props.endHour',
      label: 'End Hour',
      category: 'validation',
      valueType: 'number',
      description: 'End hour for time range',
    },
    {
      key: 'props.interval',
      label: 'Interval',
      category: 'validation',
      valueType: 'number',
      description: 'Time interval in minutes',
    },
  ],

  // ── File Upload ──
  customfileupload: [
    {
      key: 'props.allowedFormats',
      label: 'Allowed Formats',
      category: 'validation',
      valueType: 'any',
      description: 'Dynamic file format list',
    },
    {
      key: 'props.maximumUploadSize',
      label: 'Max File Size',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic max upload size in bytes',
    },
    {
      key: 'props.minimumUploadSize',
      label: 'Min File Size',
      category: 'validation',
      valueType: 'number',
      description: 'Dynamic min upload size in bytes',
    },
    {
      key: 'props.multiple',
      label: 'Multiple Files',
      category: 'state',
      valueType: 'boolean',
      description: 'Allow multiple file uploads',
    },
  ],

  // ── Phone Number ──
  customphonenumber: [
    {
      key: 'props.initialCountry',
      label: 'Initial Country',
      category: 'value',
      valueType: 'any',
      description: 'Default country for phone input',
    },
    {
      key: 'props.allowDropdown',
      label: 'Allow Dropdown',
      category: 'state',
      valueType: 'boolean',
      description: 'Allow country code dropdown',
    },
    {
      key: 'props.preferredCountries',
      label: 'Preferred Countries',
      category: 'value',
      valueType: 'any',
      description: 'Preferred country list',
    },
  ],

  // ── Currency Input ──
  customcurrencyformatinput: [
    {
      key: 'props.min',
      label: 'Min Value',
      category: 'validation',
      valueType: 'number',
      description: 'Minimum currency value',
    },
    {
      key: 'props.max',
      label: 'Max Value',
      category: 'validation',
      valueType: 'number',
      description: 'Maximum currency value',
    },
  ],

  // ── Location Widget ──
  customlocationwidget: [
    {
      key: 'props.locationFieldContentConfig',
      label: 'Location Config',
      category: 'value',
      valueType: 'any',
      description: 'Location widget configuration',
    },
  ],

  // ── NID / ID Fields ──
  customidinput: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable ID prefetch',
    },
  ],
  nid: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable ID prefetch',
    },
  ],
  customcrvsidinput: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable CRVS ID prefetch',
    },
  ],
  customtininput: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable TIN prefetch',
    },
  ],
  customdoubleidinput: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable double ID prefetch',
    },
  ],
  customapplicationnumberinput: [
    {
      key: 'props.usePrefetch',
      label: 'Use Prefetch',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable app number prefetch',
    },
  ],

  // ── Data Fetch Fields ──
  customgenericdatafetch: [
    {
      key: 'props.url',
      label: 'API URL',
      category: 'value',
      valueType: 'string',
      description: 'Dynamic API endpoint URL',
    },
    {
      key: 'props.useBaseUrl',
      label: 'Use Base URL',
      category: 'value',
      valueType: 'boolean',
      description: 'Use base API URL',
    },
  ],
  customotpdatafetch: [
    {
      key: 'props.url',
      label: 'API URL',
      category: 'value',
      valueType: 'string',
      description: 'Dynamic API endpoint URL',
    },
  ],

  // ── Table Fields ──
  customdatasettable: [
    {
      key: 'props.isSelectable',
      label: 'Selectable',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable row selection',
    },
    {
      key: 'props.maxSelectable',
      label: 'Max Selectable',
      category: 'validation',
      valueType: 'number',
      description: 'Maximum selectable rows',
    },
    {
      key: 'props.isSearchVisible',
      label: 'Show Search',
      category: 'state',
      valueType: 'boolean',
      description: 'Show table search',
    },
    {
      key: 'props.isSortVisible',
      label: 'Show Sort',
      category: 'state',
      valueType: 'boolean',
      description: 'Show sort controls',
    },
    {
      key: 'props.paginationVisible',
      label: 'Show Pagination',
      category: 'state',
      valueType: 'boolean',
      description: 'Show pagination',
    },
  ],
  customdatatable: [
    {
      key: 'props.searchable',
      label: 'Searchable',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable table search',
    },
    {
      key: 'props.selectable',
      label: 'Selectable',
      category: 'state',
      valueType: 'boolean',
      description: 'Enable row selection',
    },
  ],

  // ── Slot Booking ──
  customslotbooking: [
    {
      key: 'props.scheduleId',
      label: 'Schedule ID',
      category: 'value',
      valueType: 'string',
      description: 'Dynamic schedule ID',
    },
  ],

  // ── Repeater Fields ──
  customrepeater: [
    {
      key: 'props.addText',
      label: 'Add Button Text',
      category: 'appearance',
      valueType: 'string',
      description: 'Text for add item button',
    },
    {
      key: 'props.maxItems',
      label: 'Max Items',
      category: 'validation',
      valueType: 'number',
      description: 'Maximum repeater items',
    },
    {
      key: 'props.minItems',
      label: 'Min Items',
      category: 'validation',
      valueType: 'number',
      description: 'Minimum repeater items',
    },
  ],
  customdynamicrepeater: [
    {
      key: 'props.addText',
      label: 'Add Button Text',
      category: 'appearance',
      valueType: 'string',
      description: 'Text for add item button',
    },
  ],

  // ── Section Message ──
  customsectionmessagewithlink: [
    {
      key: 'props.message',
      label: 'Message',
      category: 'appearance',
      valueType: 'string',
      description: 'Dynamic message text',
    },
    {
      key: 'props.linkText',
      label: 'Link Text',
      category: 'appearance',
      valueType: 'string',
      description: 'Dynamic link text',
    },
    {
      key: 'props.linkUrl',
      label: 'Link URL',
      category: 'appearance',
      valueType: 'string',
      description: 'Dynamic link URL',
    },
  ],
};

export function getExpressionPropsForType(
  fieldType: string,
  fieldKey: string
): IExpressionProperty[] {
  const props = [...STANDARD_EXPRESSION_PROPS];
  if (TYPE_EXPRESSION_PROPS[fieldType]) {
    props.push(...TYPE_EXPRESSION_PROPS[fieldType]);
  }
  // Always add model.FIELDKEY for setting field value
  props.push({
    key: `model.${fieldKey}`,
    label: 'Field Value',
    category: 'value',
    valueType: 'any',
    description: `Set the value of this field (${fieldKey})`,
  });
  return props;
}
